import {Polygon} from '../models/polygon';
import { Injectable, inject, signal} from '@angular/core';
import {GeometryType} from '../enums/geometry-type.enum';
import {BaseGeometry} from '../models/base-geometry';
import {BasePoint} from '../models/base-point';
import {BehaviorSubject, Subject, Subscription} from 'rxjs';
import {Logger} from '../../common/utils/logger';
import {SettingsService} from './settings.service';
import {TileProvider} from '../map/tile-providers/tile-provider';
import {MapType} from '../enums/map-type';
import {ObjectUtil} from '../../common/utils/object.util';
import {SettingsData} from '../models/settings-data';
import {LocalSettings} from "../models/local-settings";

/**
 * What the rest of the app may ask of the 3D view (Map3dComponent) while
 * there is one: the screenshot and the export's map picture redraw it without
 * the comments, frame it for the report and wait for it to settle before
 * photographing it (MapCaptureService).
 */
export interface MapView3d {
  /** Redraws everything from the geometries and the settings as they are now. */
  syncGeometries(): void;
  /** Turns the camera on all of these; resolves once it is there. */
  frameGeometries(geometries: BaseGeometry[]): Promise<void>;
  /** Resolves once the view has stopped moving and its tiles are in, or after `timeoutMs`. */
  settled(timeoutMs: number): Promise<void>;
  /** Remembers the camera; the function returned puts it back. */
  holdCamera(): () => void;
}

@Injectable({
  providedIn: 'root'
})
export class MapService {
  private settings = inject(SettingsService);


  private readonly mapObserve = new BehaviorSubject<google.maps.Map>(null);
  private overlayView: google.maps.OverlayView;
  private tileProvider: TileProvider;
  private currentSettings: SettingsData;
  private currentLocalSettings: LocalSettings;
  isDraggingMap = false
  crosshairEnabled = false;
  /**
   * Whether an info window is open, which is what stops a click landing on
   * the geometry underneath it.
   *
   * A signal behind a plain property. Three components write this and two
   * templates read it eighteen times, and one of the writes happens in a
   * .then() after a comment is deleted -- an async continuation, which is
   * exactly where a plain field stops reaching the components that read it.
   *
   * The facade is deliberate: reading through the getter still registers
   * the dependency, so all eighteen bindings keep working untouched, while
   * writes now go through set() and notify. Converting the call sites
   * instead would have meant eighteen chances to leave a bare function
   * reference in a template, where it reads as permanently true.
   */
  private readonly infoWindowShownSignal = signal(false);

  get infoWindowShown(): boolean {
    return this.infoWindowShownSignal();
  }

  set infoWindowShown(value: boolean) {
    this.infoWindowShownSignal.set(value);
  }
  private eventListeners: google.maps.MapsEventListener[] = [];

  /**
   * Height of the last surface clicked on the 3D map, in metres above SEA
   * LEVEL, as the 3D map gives it. A height field above the ground (the
   * altitude3d ones) takes it less the ground's height there, never as is.
   *
   * Kept here rather than on the canvas because the polygon panel needs it and
   * the two do not otherwise talk: it lets someone set a roof's height by
   * clicking the roof instead of guessing a number.
   */
  lastPickedAltitude = 0;

  /**
   * The app moved the map on purpose (panTo, fitBounds, focusOnGeometries):
   * zoom to a shape, a search result, my location. The canvas carries these
   * into the 3D view, which does not follow the flat map by itself once the
   * user has moved the 3D camera. Carries the geometries when it was those
   * that were zoomed to: the 3D view frames them itself (see follow2dFocus).
   */
  readonly onFocus = new Subject<BaseGeometry[] | void>();

  /** The 3D view while the canvas has one (it registers itself), else null. */
  view3d: MapView3d | null = null;


  /**
   * The little map at the top of an info sidebar.
   *
   * A getter, not a constant, because it has to answer differently in light
   * and dark. It used to be a fixed object with a `styles` array and no dark
   * variant, so the preview stayed bright white inside a dark panel.
   *
   * Deliberately no `mapId`. A cloud map id takes over styling completely and
   * makes `styles` inert -- which is why the three sidebars that inline their
   * own options with one could not be themed at all, and why they now use
   * this instead.
   */
  get SIDENAV_MAP_OPTIONS(): google.maps.MapOptions {
    // ONE object, for good. A new [options] input makes <google-map> call
    // setOptions with its own default centre merged in: a new object on every
    // read froze the polygon panel in a change-detection loop, and a new one
    // per theme put every open preview back on Mountain View when the theme
    // changed. The theme's styles are applied to the live maps instead: a
    // panel hands its map to themeSidenavMap() when it is ready.
    if (!this.sidenavMapOptions) {
      this.sidenavMapOptions = {
        disableDefaultUI: true,
        draggable: false,
        gestureHandling: 'none',
        clickableIcons: false,
        disableDoubleClickZoom: true,
        scaleControl: false,
        zoomControl: false,
        streetViewControl: false,
        mapTypeId: 'roadmap',
        styles: this.previewMapStyles,
        // Fitted to a shape at whole zoom levels only, a preview showed it at
        // anything from half to all of its height: the next level up would
        // not have fitted. In between, it fills the preview.
        isFractionalZoomEnabled: true,
      };
    }
    return this.sidenavMapOptions;
  }

  private sidenavMapOptions: google.maps.MapOptions = null;
  private sidenavMaps = new Set<google.maps.Map>();
  private sidenavThemeObserver: MutationObserver = null;

  /**
   * Keeps a sidebar's preview map in the theme: styles it now, and again
   * whenever the body's theme class changes, without touching its position.
   */
  themeSidenavMap(map: google.maps.Map) {
    map.setOptions({styles: this.previewMapStyles});
    // Every panel open brings a new map, and the ones from closed panels were
    // only dropped on a theme change: until then each kept its map and its
    // detached DOM alive. A map whose div has left the page is gone for good.
    this.sidenavMaps.forEach(m => {
      if (m !== map && !m.getDiv().isConnected) {
        this.sidenavMaps.delete(m);
      }
    });
    this.sidenavMaps.add(map);
    if (!this.sidenavThemeObserver && typeof MutationObserver !== 'undefined') {
      let dark = MapService.isDarkTheme();
      this.sidenavThemeObserver = new MutationObserver(() => {
        if (MapService.isDarkTheme() === dark) {
          return;
        }
        dark = MapService.isDarkTheme();
        this.sidenavMaps.forEach(m => {
          if (!m.getDiv().isConnected) {
            this.sidenavMaps.delete(m);
          } else {
            m.setOptions({styles: this.previewMapStyles});
          }
        });
      });
      this.sidenavThemeObserver.observe(document.body, {attributes: true, attributeFilter: ['class']});
    }
  }

  /**
   * Just the styles, for the previews that keep their own options.
   *
   * Three of the five info sidebars set a centre and a zoom of their own and
   * cannot take the whole options object, but they still have to follow the
   * theme -- so they take this and drop their mapId, which is what was
   * making their styles inert.
   */
  get previewMapStyles(): google.maps.MapTypeStyle[] {
    return MapService.isDarkTheme()
      ? MapService.DARK_PREVIEW_STYLE : MapService.LIGHT_PREVIEW_STYLE;
  }

  /**
   * Whether the app is currently dark.
   *
   * Read off the body class rather than from ThemeService, because that is
   * where the theme actually lands and this is called from a getter that
   * runs during change detection -- injecting a service here would make the
   * map options depend on Angular's construction order.
   */
  private static isDarkTheme(): boolean {
    return typeof document !== 'undefined'
      && document.body.classList.contains('dark-theme');
  }

  /**
   * No points of interest: a preview is about the shape, not about what is
   * near it. Only business pins were off, so parks, landmarks, schools and
   * stations kept their pins and names -- and in the point and spot panels,
   * which could still click them, a click opened Google's card and moved the
   * preview. The ground they stand on (a park's green) stays.
   */
  private static readonly NO_POI_STYLE: google.maps.MapTypeStyle[] = [
    {featureType: 'poi', elementType: 'labels', stylers: [{visibility: 'off'}]},
    {featureType: 'poi.business', stylers: [{visibility: 'off'}]},
    {featureType: 'transit.station', elementType: 'labels.icon', stylers: [{visibility: 'off'}]},
  ];

  private static LIGHT_PREVIEW_STYLE: google.maps.MapTypeStyle[] = [
    ...MapService.NO_POI_STYLE,
  ];

  /**
   * The same map, dark.
   *
   * Muted rather than inverted: the preview sits behind coloured geometry
   * and a bright basemap fights it. Water stays distinguishable from land
   * because a plot beside a river is a different plot.
   */
  private static DARK_PREVIEW_STYLE: google.maps.MapTypeStyle[] = [
    ...MapService.NO_POI_STYLE,
    {elementType: 'geometry', stylers: [{color: '#212a33'}]},
    {elementType: 'labels.text.stroke', stylers: [{color: '#212a33'}]},
    {elementType: 'labels.text.fill', stylers: [{color: '#8b98a5'}]},
    {featureType: 'road', elementType: 'geometry', stylers: [{color: '#2c3742'}]},
    {featureType: 'road', elementType: 'geometry.stroke', stylers: [{color: '#212a33'}]},
    {featureType: 'road', elementType: 'labels.text.fill', stylers: [{color: '#9aa7b4'}]},
    {featureType: 'road.highway', elementType: 'geometry', stylers: [{color: '#3a4753'}]},
    {featureType: 'water', elementType: 'geometry', stylers: [{color: '#17202a'}]},
    {featureType: 'water', elementType: 'labels.text.fill', stylers: [{color: '#4d5d6b'}]},
    {featureType: 'poi', elementType: 'geometry', stylers: [{color: '#26303a'}]},
    {featureType: 'poi', elementType: 'labels.text.fill', stylers: [{color: '#7d8a97'}]},
    {featureType: 'poi.park', elementType: 'geometry', stylers: [{color: '#1f2c26'}]},
    {featureType: 'transit', stylers: [{visibility: 'off'}]},
    {featureType: 'administrative', elementType: 'geometry',
      stylers: [{color: '#3a4753'}]},
  ];
  private settingsSub: Subscription;
  private settingsLocalSub: Subscription;
  /**
   * The map that was set up, kept past `map = null`. The canvas lets go of its
   * map before calling destroy(), so destroy() reading `this.map` found null
   * and the listeners on the old map -- which hold the closed canvas and its
   * shapes -- were never removed.
   */
  private initializedMap: google.maps.Map = null;
  /** The start-up map type switches, cleared by destroy(). */
  private startupTimers: ReturnType<typeof setTimeout>[] = [];

  set map(map: google.maps.Map) {
    Logger.log('MapService - set map', !!map);
    if (map) {
      this.initializedMap = map;
      this.initMapTypes(map);
      this.initOverlayView(map);
      this.initEvents(map);
    }
    this.mapObserve.next(map);
  }

  get map(): google.maps.Map {
    return this.mapObserve.value;
  }

  get mapObservable() {
    return this.mapObserve;
  }

  private initEvents(map: google.maps.Map) {
    Logger.log('MapService - initEvents');
    this.eventListeners.push(google.maps.event.addListener(map, 'dragstart', event => {
      this.isDraggingMap = true
    }));
    this.eventListeners.push(google.maps.event.addListener(map, 'dragend', event => {
      this.isDraggingMap = false
    }));
    this.eventListeners.push(google.maps.event.addListener(map, 'dragleave', event => {
      this.isDraggingMap = false
    }));
  }

  destroy(){
    // Left running, these fired on the closed canvas's map a few seconds
    // after a quick exit (a wrong project, a link to one that is gone) and
    // threw on the tile provider set to null below.
    this.startupTimers.forEach(timer => clearTimeout(timer));
    this.startupTimers = [];
    this.eventListeners.forEach(listener => {
      listener.remove();
    });
    const map = this.map ?? this.initializedMap;
    if(map) {
      google.maps.event.clearInstanceListeners(map);
      // The data layer (parcels) keeps listeners of its own.
      google.maps.event.clearInstanceListeners(map.data);
      map.setMapTypeId('roadmap');
    }
    this.initializedMap = null;
    this.eventListeners = [];

    if(this.tileProvider){
      this.tileProvider.clearCache();
    }
    this.tileProvider = null;
    this.overlayView = null;
    this.settings.setLocalValue('showTrafficLayer', false);
    this.settingsSub?.unsubscribe();
    this.settingsLocalSub?.unsubscribe();
    this.settingsSub = null;
    this.settingsLocalSub = null;
    // Held Shift belongs to the canvas being left: kept on, every shape on the
    // next one opened was unclickable until Shift was pressed again.
    this.crosshairEnabled = false;
  }

  initMapTypes(map: google.maps.Map) {
    const mapTypes = map.mapTypes;
    if(!this.tileProvider){
      this.tileProvider = new TileProvider(this.settings);
    }
    mapTypes.set(TileProvider.ID, this.tileProvider);

    this.currentSettings = ObjectUtil.clone(this.settings.data);
    this.currentLocalSettings = ObjectUtil.clone(this.settings.local);

    // A map set again without a destroy() in between must not double these up.
    this.settingsSub?.unsubscribe();
    this.settingsLocalSub?.unsubscribe();
    this.startupTimers.forEach(timer => clearTimeout(timer));
    this.startupTimers = [];
    this.settingsSub = this.settings.onSettingsChanged.subscribe(data => {
      if (data) {
        Logger.log('onSettingsChanged', data.isDefault, data.mapSource, map.getMapTypeId().toString(), data);
        if (this.currentSettings && (data.mapSource !== this.currentSettings.mapSource || this.currentSettings.mapType !== data.mapType)) {
          this.refreshMap()
        }
        this.currentSettings = ObjectUtil.clone(data)
      }
    });

    this.settingsLocalSub = this.settings.onLocalSettingChanged.subscribe(changed => {
      // The theme and HD setting are read into each Google tile's URL, so the
      // tiles already on screen have to be redrawn too, or the map ends up a
      // checkerboard of old and new tiles until the next zoom.
      if (changed && ['showTrafficLayer', 'theme', 'showHDMaps'].includes(changed.key)
        && this.currentLocalSettings && this.currentLocalSettings[changed.key] !== changed.value) {
        this.refreshMap()
      }
      this.currentLocalSettings = ObjectUtil.clone(this.settings.local);
      Logger.log('MapService - onLocalSettingChanged 2');
    });


    this.refreshMap(map);
    this.startupTimers.push(setTimeout(() => {
      map.setMapTypeId('roadmap')
      this.startupTimers.push(setTimeout(() => {
        this.refreshMap(map);
      }, 1000));
    }, 2000));
    // map.setMapTypeId(TileProvider.ID);
    // this.refreshMap(map);

  }

  private initOverlayView(map: google.maps.Map) {
    if(!this.overlayView) {
      this.overlayView = new google.maps.OverlayView();
      this.overlayView.setMap(map);
    }
  }

  getTileProviderMaxZoom(){
    return this.tileProvider ? this.tileProvider.getMaxZoom() : 21;
  }

  fromContainerPixelToLatLng(x: number, y: number) {
    if(!this.overlayView) return new google.maps.LatLng(0, 0);
    const p = this.overlayView.getProjection();
    return p ? p.fromContainerPixelToLatLng(new google.maps.Point(x, y)) : new google.maps.LatLng(0, 0);
  }

  fromLatLngToContainerPixel(lat: number, lng: number) {
    if(!this.overlayView) return new google.maps.Point(0, 0);
    const p = this.overlayView.getProjection();
    return p ? p.fromLatLngToContainerPixel(new google.maps.LatLng(lat, lng)) : new google.maps.Point(0, 0);
  }

  computePixelDistanceBetween(lat1: number, lng1: number, lat2: number, lng2: number) {
    const l1 = this.fromLatLngToContainerPixel(lat1, lng1);
    const l2 = this.fromLatLngToContainerPixel(lat2, lng2);
    return Math.sqrt(Math.pow(l2.x - l1.x, 2) + Math.pow(l2.y - l1.y, 2))
  }

  // getMapTypeId() {
  //   return this.map.getMapTypeId();
  // }

  // setMapTypeId(id: string) {
  //   this.setMapTypeIdNow('');
  //   setTimeout(() => {
  //     this.setMapTypeIdNow(id);
  //   }, 10)
  // }
  //
  // private setMapTypeIdNow(id: string) {
  //   const map = this.map;
  //   map.setMapTypeId(id);
  //   this.refreshMap();
  //   Logger.log('setMapTypeIdNow', id)
  // }

  /**
   * Credit for the tile layer on screen, or empty when it needs none.
   *
   * The national services are free to use commercially only on condition
   * the source is named, so this is part of using them rather than a
   * nicety. Google draws its own, which is why its entry is blank.
   * With the traffic layer on, refreshMap shows Google's own map types
   * instead of the chosen source, so there is nothing of it to credit.
   */
  getTileAttribution(): string {
    if (!this.tileProvider || this.settings.local.showTrafficLayer) {
      return '';
    }
    return this.tileProvider.getAttribution();
  }

  getTileProvider(): TileProvider {
    return this.tileProvider;
  }

  getTileUrl(x: number, y: number, zoom: number, mapType?: MapType): string {
    return this.tileProvider.getUrl(x, y, zoom, mapType)
  }

  refreshMap(map: google.maps.Map = this.map) {
    // No tile provider: destroy() has run and the canvas is gone.
    if(!map || !this.tileProvider) return;
    Logger.log('refreshMap');
      if(this.settings.local.showTrafficLayer){
        switch (this.settings.data.mapType) {
          case MapType.SATELLITE:
            map.setMapTypeId('hybrid');
            break;
          case MapType.TERRAIN:
            map.setMapTypeId('terrain');
            break;
          case MapType.HYBRID:
            map.setMapTypeId('hybrid');
            break;
          default:
            map.setMapTypeId('roadmap');

        }
      }else{
        map.setMapTypeId(TileProvider.ID);
        this.tileProvider.refreshAll();
      }
  }

  setCrosshairEnabled(enabled: boolean) {
    this.crosshairEnabled = enabled;
    // Shift can come before the next canvas has its map (the last one's is nulled on leaving).
    this.map?.data.setStyle(this.getParcelsDataStyle());
  }

  clearDataLayer() {
    const dl = this.map.data;
    dl.forEach(f => {
      dl.remove(f);
    })
  }

  addDataLayer(data: any, style: any) {
    const dl = this.map.data;
    dl.addGeoJson(data);
    dl.setStyle(style);
  }

  addParcelsDataLayer(data: any) {
    this.addDataLayer(data, this.getParcelsDataStyle());
  }

  getParcelsDataStyle() {
    return {
      clickable: !this.crosshairEnabled,
      fillOpacity: 0.1,
      fillColor: '#0153f6',
      strokeColor: '#0153f6',
      strokeWeight: 1
    };
  }

  focusOnGeometries(...geometries: BaseGeometry[]) {
    if (!this.map || geometries.length === 0) {
      return;
    }

    const first = geometries[0];
    if (geometries.length === 1 && (first.type === GeometryType.TYPE_POINT || first.type === GeometryType.TYPE_SPOT)) {
      this.map.setZoom(Math.min(Math.max(this.map.getZoom(), 18), 21));
      this.map.panTo((first as BasePoint).latLng);
      this.onFocus.next(geometries);
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    geometries.forEach(geo => {
      if (geo.type === GeometryType.TYPE_POINT || geo.type === GeometryType.TYPE_SPOT) {
        bounds.extend((geo as BasePoint).latLng);
      } else if (geo.type === GeometryType.TYPE_POLYGON) {
        // The outline, not the stored points: a circle stores only its centre
        // and one point on the rim, which framed a quarter of it.
        (geo as Polygon).getPath().forEach(latLng => {
          bounds.extend(latLng);
        });
      }
    });
    if (bounds.isEmpty()) {
      return;
    }

    this.map.fitBounds(bounds.toJSON(), 50);
    this.onFocus.next(geometries);
  }

  panTo(latLng: google.maps.LatLngLiteral, zoom: number = -1) {
    if (zoom > 0) {
      this.map.setZoom(zoom);
    }
    this.map.panTo(latLng);
    this.onFocus.next();
  }


  fitBounds(bounds: google.maps.LatLngBounds | google.maps.LatLngBoundsLiteral, padding: number | google.maps.Padding = 10) {
    this.map.fitBounds(bounds, padding);
    this.onFocus.next();
  }

  getCenter() {
    return this.map.getCenter();
  }

  /** Null until the canvas has a map -- settings can ask before it exists. */
  getBounds() {
    return this.map ? this.map.getBounds() : null;
  }

  resetCamera() {
    this.map.setTilt(0);
    this.map.setHeading(0);
    // this.map.moveCamera({
    //   tilt: 0,
    //   heading: 0
    // })
  }

  /**
   * The map's + and - buttons, which stay up in 3D. A deliberate move like
   * any other (onFocus): once the user had turned the 3D view, these zoomed
   * only the flat map under it, and the next turn of the 3D view put the old
   * zoom back. The 3D camera follows the flat map's new zoom instead.
   */
  zoomIn() {
    this.map.setZoom(this.map.getZoom() + 1);
    this.onFocus.next();
  }

  zoomOut() {
    this.map.setZoom(this.map.getZoom() - 1);
    this.onFocus.next();
  }
}
